import { TItemDisabledInfo, TSearchOption, TSearchValue } from "@src/components/intelligence-search/search-template";
import { InputMentionValue, SmlAsset, SmlJsonQuery, SmlJsonQueryFilter } from "@src/types/intelligence-search";
import React, { useLayoutEffect, useState } from "react";
import { useFetchNextOption } from "./use-fetch-next-options";
import { uniqBy } from "lodash";
import { useManageAddNewMetric } from "./use-manage-add-new-metric";
import { TSearchCustomMetricTemplateProps } from "@src/components/intelligence-search/search-custom-metric";

type PotentialAssetMeta = {
    meta: SmlAsset[];
    unlikely_meta: SmlAsset[];
    selected_assets: SmlAsset[];
};
export const useManageSearchTemplate = ({
    smtJsonQueryPayload,
    additionalOnChange,
    isPreventFetchApi,
    labelProps,
    productCode,
    startDate,
    endDate,
    addNewOptionProps,
}: {
    productCode?: string;
    startDate?: string;
    endDate?: string;
    smtJsonQueryPayload?: SmlJsonQuery;
    additionalOnChange?: (values: TSearchValue[]) => void;
    isPreventFetchApi?: boolean;
    labelProps?: {
        transformValueToFilter?: (values: TSearchValue[]) => any[];
        disabled?: (metaItem: SmlAsset, currentFilterValues: any[]) => TItemDisabledInfo;
    };
    addNewOptionProps?: {
        handleFetchDryRun: (
            filters: SmlJsonQueryFilter[],
            dryRunCheck: (payloadJsonQuery: SmlJsonQuery) => Promise<any>,
        ) => Promise<any>;
        currentFilterValues: SmlJsonQueryFilter[];
        onSuccess: (newFilterSelected: SmlJsonQueryFilter[]) => void;
    };
}) => {
    const [values, setValues] = useState<TSearchValue[]>([]);
    const [options, setOptions] = useState<TSearchOption[]>([]);

    const handleChange = (values: TSearchValue[]) => {
        setValues(values);
        additionalOnChange?.(values);
    };

    const [assetMetaState, setAssetMetaState] = useState<PotentialAssetMeta>({
        meta: [],
        unlikely_meta: [],
        selected_assets: [],
    });

    const { fetchAnalyze, isLoadingAnalyze, fetchDryrun } = useFetchNextOption({
        productCode,
        startDate,
        endDate,
    });

    const filterValue = React.useMemo(() => {
        return labelProps?.transformValueToFilter?.(values) ?? [];
    }, [values]);

    const disabledLabel = React.useCallback(
        (metaItem: SmlAsset) => {
            return labelProps?.disabled?.(metaItem, filterValue);
        },
        [filterValue],
    );

    useLayoutEffect(() => {
        setOptions(
            convertAssetMetaToOption(
                uniqBy(
                    [...assetMetaState.selected_assets, ...assetMetaState.unlikely_meta, ...assetMetaState.meta],
                    (item) => item.id,
                ),
                disabledLabel,
            ),
        );
    }, [assetMetaState, disabledLabel]);

    useLayoutEffect(() => {
        if (isPreventFetchApi || !smtJsonQueryPayload) return;

        fetchAnalyze(smtJsonQueryPayload).then((data) => {
            setAssetMetaState(data);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify([smtJsonQueryPayload, isPreventFetchApi])]);

    const optionsAsMetric = options
        .filter((opt) => opt.meta.asset_type === "metric")
        .map((opt) => ({
            id: opt.meta.name,
            display: opt.meta.title,
            meta: opt.meta,
        }));

    const mergeFormulaIntoCurrentFilterValues = (
        formula: string,
        alias: string,
        mentionMeta: InputMentionValue,
        editProps?: {
            id: string;
        },
    ) => {
        if (!addNewOptionProps?.currentFilterValues) return [];
        if (editProps) {
            return addNewOptionProps?.currentFilterValues?.map((item: SmlJsonQueryFilter) => {
                if (item.meta?.id === editProps.id) {
                    return {
                        ...item,
                        name: alias,
                        meta: { ...item.meta, id: formula, name: alias, title: alias, ...mentionMeta },
                    };
                }
                return item;
            });
        }
        const newMember: SmlJsonQueryFilter = {
            name: alias,
            isCustomMetric: true,
            meta: {
                id: formula,
                name: alias,
                title: alias,
                asset_type: "metric",
                data_type: "float",
                metadata: {},
                description: undefined,
                ...mentionMeta,
            },
        };
        return [...addNewOptionProps.currentFilterValues, newMember];
    };
    const handleCheckDryRun = ({
        formula,
        alias,
        mentionMeta,
        editProps,
    }: {
        formula: string;
        alias: string;
        mentionMeta: InputMentionValue;
        editProps?: { id: string };
    }) => {
        const filterSelected = mergeFormulaIntoCurrentFilterValues(formula, alias, mentionMeta, editProps) ?? [];
        return {
            check: () => {
                return addNewOptionProps?.handleFetchDryRun?.(filterSelected, (payloadJsonQuery) => {
                    return fetchDryrun(payloadJsonQuery);
                });
            },
            onSuccess: (fieldType: string) => {
                const newFilterSelected: SmlJsonQueryFilter[] = filterSelected.map((item) => {
                    if (item.name === alias) {
                        return { ...item, data_type: fieldType.toLowerCase() };
                    }
                    return item;
                });
                addNewOptionProps?.onSuccess(newFilterSelected);
            },
        };
    };
    const { loading, handleConfirmCustomMetric } = useManageAddNewMetric({
        dryRunCheck: handleCheckDryRun,
    });
    return {
        values,
        setValues,
        options,
        optionsLoading: isLoadingAnalyze,
        handleChange,
        addNewOptionProps: {
            additionalValidator: {
                alias: (value, initValue) => {
                    return new Promise((resolve, reject) => {
                        if (initValue && initValue === value) {
                            resolve();
                            return;
                        }
                        if (values.some((v) => v.name === value)) {
                            reject(new Error("This alias is already in use. Please choose a unique name"));
                        }
                        resolve();
                    });
                },
            },
            handleConfirm: handleConfirmCustomMetric,
            loading,
            options: optionsAsMetric,
        } as TSearchCustomMetricTemplateProps,
    };
};

const convertAssetMetaToOption = (
    assetsMeta: SmlAsset[],
    disabled?: (metaItem: SmlAsset) => TItemDisabledInfo | undefined,
): TSearchOption[] => {
    function disabledInfoFn(_assetMeta: SmlAsset) {
        const { disabledLabelOnly, disabledFilterOnly, isCustomMetric, disabledFull, hasDisabled, unlikelyReasons } =
            disabled?.(_assetMeta) ?? {};

        return {
            isCustomMetric,
            disabledFull: disabledFull,
            disabledLabelOnly,
            disabledFilterOnly,
            hasDisabled: hasDisabled,
            unlikelyReasons: unlikelyReasons,
        };
    }
    return assetsMeta
        .map((_assetMeta) => {
            const disabledInfo = disabledInfoFn(_assetMeta);
            return {
                ..._assetMeta,
                ...disabledInfo,
            };
        })
        .sort((a, b) => {
            if (a.hasDisabled) return 1;
            if (b.hasDisabled) return -1;
            return 0;
        })
        .map((_assetMeta) => {
            return {
                label: _assetMeta.name,
                value: _assetMeta.name,
                meta: _assetMeta as any,
            };
        });
};
